import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { getToken } from '../api';
import {
  CLOSE_DESKTOP_SESSION_GONE,
  DESKTOP_PROTOCOL_VERSION,
  DesktopConfigFrame,
  DesktopCursorFrame,
  DesktopInboundFrame,
  DesktopMediaFrame,
  DesktopOutboundFrame,
  DesktopProtocolError,
  WS_VIEWER_PATH,
  decodeDesktopMediaFrame,
  describeDesktopClose,
  describeDesktopConsent,
  encodeDesktopFrame,
  parseDesktopControlFrame,
} from './wire';

export type DesktopSessionState =
  | 'connecting'
  /** A customer PC is showing its Yes/No box; nothing is captured yet. */
  | 'waiting-for-consent'
  | 'waiting-for-config'
  | 'waiting-for-keyframe'
  | 'streaming'
  | 'error'
  | 'closed';

/** Media has stopped for long enough that the child is probably re-attaching. */
const STALL_MS = 2_000;
/** Status-line cadence. Publishing fps per frame would re-render at frame rate. */
const TICK_MS = 1_000;

export interface DesktopSessionOptions {
  /** Null holds the socket closed — the dialog can mount before the POST returns. */
  sessionId: string | null;
  /** Called with decodable frames only: deltas before the first IDR never arrive. */
  onMedia: (frame: DesktopMediaFrame) => void;
  onCursor?: (cursor: DesktopCursorFrame) => void;
}

export interface DesktopSession {
  state: DesktopSessionState;
  error: string | null;
  config: DesktopConfigFrame | null;
  /** While `waiting-for-consent`: how long the box gives the person to answer. */
  consentTimeoutMs: number | null;
  /** Frames handed to the caller in the last second. */
  fps: number;
  /** No media for STALL_MS — the box dropped, or the link is wedged. */
  stalled: boolean;
  /** Latest remote cursor. A ref, so cursor traffic never re-renders the tree. */
  cursorRef: RefObject<DesktopCursorFrame | null>;
  send: (frame: DesktopOutboundFrame) => void;
  requestKeyframe: () => void;
}

/**
 * Viewer half of the desktop stream: one wss to the broker, per-frame acks, and
 * the IDR gate. Decoding and painting belong to the caller — this hook never
 * touches WebCodecs, so the socket keeps running while a decoder is rebuilt.
 */
export function useDesktopSession({
  sessionId,
  onMedia,
  onCursor,
}: DesktopSessionOptions): DesktopSession {
  const [state, setState] = useState<DesktopSessionState>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<DesktopConfigFrame | null>(null);
  const [consentTimeoutMs, setConsentTimeoutMs] = useState<number | null>(null);
  const [fps, setFps] = useState(0);
  const [stalled, setStalled] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const cursorRef = useRef<DesktopCursorFrame | null>(null);
  /** A delta fed to a decoder with no IDR renders garbage or throws outright. */
  const needKeyframeRef = useRef(true);
  const framesRef = useRef(0);
  const lastMediaAtRef = useRef(0);

  // Held in a ref so a caller passing inline callbacks does not tear down and
  // rebuild the socket on every render.
  const handlersRef = useRef({ onMedia, onCursor });
  handlersRef.current = { onMedia, onCursor };

  const send = useCallback((frame: DesktopOutboundFrame) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(encodeDesktopFrame(frame));
  }, []);

  const requestKeyframe = useCallback(() => {
    needKeyframeRef.current = true;
    setState((current) => (current === 'streaming' ? 'waiting-for-keyframe' : current));
    send({ type: 'desktop_keyframe_request' });
  }, [send]);

  useEffect(() => {
    if (!sessionId) return;
    const id = sessionId;
    const jwt = getToken();
    if (!jwt) {
      setError('Your sign-in expired. Sign in again and reopen the screen.');
      setState('error');
      return;
    }

    let disposed = false;
    needKeyframeRef.current = true;
    framesRef.current = 0;
    lastMediaAtRef.current = 0;
    setState('connecting');
    setError(null);
    setConfig(null);
    setConsentTimeoutMs(null);
    setStalled(false);

    const ws = new WebSocket(`${window.location.origin.replace(/^http/, 'ws')}${WS_VIEWER_PATH}`);
    ws.binaryType = 'arraybuffer';
    socketRef.current = ws;

    function post(frame: DesktopOutboundFrame): void {
      if (ws.readyState === WebSocket.OPEN) ws.send(encodeDesktopFrame(frame));
    }

    /** First failure wins: it is the specific one, the close that follows is not. */
    function fail(message: string): void {
      if (disposed) return;
      setError((current) => current ?? message);
      setState('error');
    }

    // Deliberately NO automatic desktop_bitrate_hint from
    // navigator.connection.downlink: that number is a recent-throughput guess
    // (capped at 10, and low on any idle link), and the child holds a hint as a
    // CEILING for the whole session — one wrong guess pinned a 1080p stream at
    // ~1.5 Mbps of unreadable text. Congestion is the pacer's job; the hint
    // frame stays in the protocol for a future explicit quality picker.

    function handleControl(frame: DesktopInboundFrame): void {
      switch (frame.type) {
        case 'hello_ok':
          if (frame.desktopProtocolVersion !== DESKTOP_PROTOCOL_VERSION) {
            fail(
              `That box speaks screen protocol v${frame.desktopProtocolVersion}; this dashboard speaks v${DESKTOP_PROTOCOL_VERSION}.`,
            );
            ws.close();
            return;
          }
          setState('waiting-for-config');
          post({ type: 'desktop_keyframe_request' });
          return;
        case 'desktop_config':
          // Also arrives mid-session on a resolution switch, which rebuilds the
          // decoder downstream — so the P-chain restarts from here either way.
          setConfig(frame);
          needKeyframeRef.current = true;
          setState('waiting-for-keyframe');
          post({ type: 'desktop_keyframe_request' });
          return;
        case 'desktop_cursor':
          cursorRef.current = frame;
          handlersRef.current.onCursor?.(frame);
          return;
        case 'desktop_clipboard':
          // Parsed so a legal frame cannot trip the strict codec; there is no
          // clipboard surface in this pass.
          return;
        case 'desktop_consent':
          // A customer PC asking its person. The verdict is theirs, so the only
          // thing to do here is say what is happening — and, on a No, say so
          // in words the operator will not mistake for a broken box.
          if (frame.state === 'pending') {
            setConsentTimeoutMs(frame.timeoutMs ?? null);
            setState('waiting-for-consent');
            return;
          }
          setConsentTimeoutMs(null);
          if (frame.state === 'granted') {
            setState((current) => (current === 'waiting-for-consent' ? 'waiting-for-config' : current));
            return;
          }
          fail(describeDesktopConsent(frame.state));
          return;
        case 'desktop_error':
          fail(frame.message || frame.code);
          return;
        case 'desktop_close':
          if (!disposed) setState((current) => (current === 'error' ? current : 'closed'));
          return;
      }
    }

    function handleMedia(data: ArrayBuffer): void {
      let frame: DesktopMediaFrame;
      try {
        frame = decodeDesktopMediaFrame(data);
      } catch (err) {
        fail(err instanceof DesktopProtocolError ? err.message : 'malformed video frame');
        ws.close();
        return;
      }

      // Ack before anything else, and for every frame including the ones the IDR
      // gate is about to drop. This is the sender's only credit signal; it
      // presumes loss after DESKTOP_ACK_TIMEOUT_MS, so an ack withheld until the
      // frame paints (or skipped) throttles the stream toward zero.
      post({ type: 'desktop_ack', seq: frame.seq, bytes: data.byteLength });

      if (needKeyframeRef.current) {
        if (!frame.keyframe) return;
        needKeyframeRef.current = false;
      }
      // Marked here rather than above the gate: traffic on the socket is not
      // progress. A keyframe request that goes missing leaves the gate dropping
      // deltas forever, and counting those as liveness is what would suppress
      // the retry below — the picture would stay frozen with a busy socket.
      lastMediaAtRef.current = Date.now();
      framesRef.current += 1;
      setState((current) => (current === 'streaming' ? current : 'streaming'));
      setStalled(false);
      handlersRef.current.onMedia(frame);
    }

    ws.onopen = () => {
      // The JWT rides the hello frame, never the query string: query strings
      // land in access logs and in the Referer of anything the page loads.
      post({ type: 'hello', jwt, sessionId: id, desktopProtocolVersion: DESKTOP_PROTOCOL_VERSION });
    };

    ws.onmessage = (event: MessageEvent<string | ArrayBuffer>) => {
      if (disposed) return;
      if (typeof event.data === 'string') {
        try {
          handleControl(parseDesktopControlFrame(event.data));
        } catch (err) {
          fail(err instanceof DesktopProtocolError ? err.message : 'malformed frame');
          ws.close();
        }
        return;
      }
      handleMedia(event.data);
    };

    ws.onerror = () => fail('Could not reach the broker.');

    ws.onclose = (event: CloseEvent) => {
      if (disposed) return;
      // A gone session is how a normal teardown looks from here, not a failure.
      if (event.code === 1000 || event.code === CLOSE_DESKTOP_SESSION_GONE) {
        setState((current) => (current === 'error' ? current : 'closed'));
        return;
      }
      fail(describeDesktopClose(event.code, event.reason));
    };

    const ticker = window.setInterval(() => {
      if (disposed) return;
      setFps(framesRef.current);
      framesRef.current = 0;
      if (lastMediaAtRef.current === 0) return;
      const quiet = Date.now() - lastMediaAtRef.current > STALL_MS;
      setStalled(quiet);
      if (quiet) {
        // The child may have dropped and re-attached inside its grace window, so
        // whatever it sends next references a P-chain this decoder no longer has.
        needKeyframeRef.current = true;
        post({ type: 'desktop_keyframe_request' });
      }
    }, TICK_MS);

    return () => {
      disposed = true;
      window.clearInterval(ticker);
      socketRef.current = null;
      if (ws.readyState === WebSocket.OPEN) {
        post({ type: 'desktop_close' });
        ws.close(1000, 'viewer closed');
      } else {
        ws.close();
      }
    };
  }, [sessionId]);

  return {
    state,
    error,
    config,
    consentTimeoutMs,
    fps,
    stalled,
    cursorRef,
    send,
    requestKeyframe,
  };
}
